import { useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, Download, ShieldAlert, ExternalLink } from "lucide-react";

const SRC_META = {
  FBI_DOJ: { label: "FBI / DOJ WANTED", color: "#DC2626" },
  INTERPOL_RED: { label: "INTERPOL RED", color: "#EF4444" },
  INTERPOL_YELLOW: { label: "INTERPOL YELLOW", color: "#F59E0B" },
};
const IPOL_BASE = "https://ws-public.interpol.int/notices/v1";

// Fetch INTERPOL notices FROM THE OPERATOR'S BROWSER (Interpol blocks datacenter
// IPs, but the public API allows browsers/CORS), then post batches to the backend
// which dedupes on (source, source_id).
async function fetchInterpol(color, maxPages = 6) {
  const out = [];
  for (let page = 1; page <= maxPages; page++) {
    const resp = await fetch(`${IPOL_BASE}/${color}?page=${page}&resultPerPage=160`, { headers: { Accept: "application/json" } });
    if (!resp.ok) throw new Error(`Interpol returned HTTP ${resp.status}`);
    const data = await resp.json();
    const notices = data?._embedded?.notices || [];
    notices.forEach((n) => out.push({
      source_id: n.entity_id,
      name: [n.forename, n.name].filter(Boolean).join(" "),
      image_url: n._links?.thumbnail?.href || "",
      nationalities: n.nationalities || [],
      dob: n.date_of_birth || "",
      sex: n.sex_id || "",
      url: `https://www.interpol.int/en/How-we-work/Notices/${color === "red" ? "Red-Notices/View-Red-Notices" : "Yellow-Notices/View-Yellow-Notices"}`,
    }));
    if (notices.length < 160) break;
  }
  return out;
}

export function FederalDb({ onAttach }) {
  const [stats, setStats] = useState(null);
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [src, setSrc] = useState("");
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState("");

  const loadStats = () => client.get("/ncic/federal-db/stats").then((r) => setStats(r.data)).catch(() => {});
  const search = async (query = q, source = src) => {
    setBusy(true);
    try { const { data } = await client.get("/ncic/federal-db", { params: { q: query || undefined, source: source || undefined } }); setList(data); }
    catch { toast.error("Federal DB search failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { loadStats(); search("", ""); }, []); // eslint-disable-line

  const importFbi = async () => {
    setImporting("FBI_DOJ");
    try {
      const { data } = await client.post("/ncic/federal-db/import-fbi");
      toast.success(`FBI/DOJ: ${data.new} new, ${data.updated} refreshed (no duplicates) — ${data.total_on_file} on file`);
      loadStats(); search();
    } catch (e) { toast.error(e.response?.data?.detail || "FBI import failed"); }
    finally { setImporting(""); }
  };
  const importInterpol = async (color) => {
    const source = color === "red" ? "INTERPOL_RED" : "INTERPOL_YELLOW";
    setImporting(source);
    try {
      const recs = await fetchInterpol(color);
      if (!recs.length) throw new Error("No notices returned");
      let totNew = 0, totUpd = 0;
      for (let i = 0; i < recs.length; i += 400) {
        const { data } = await client.post("/ncic/federal-db/import-batch", { source, records: recs.slice(i, i + 400) });
        totNew += data.new; totUpd += data.updated;
      }
      toast.success(`${SRC_META[source].label}: ${totNew} new, ${totUpd} refreshed (no duplicates)`);
      loadStats(); search();
    } catch (e) {
      toast.error(`Interpol fetch failed (${e.message}). Interpol allows browser access only — retry, or check your network blocks interpol.int.`);
    } finally { setImporting(""); }
  };

  return (
    <div data-testid="ncic-federaldb">
      <div className="bg-[#7C3AED] text-white font-mono text-[11px] font-bold px-3 py-1.5 mb-3">
        FEDERAL RECORD DATABASE · FBI/DOJ WANTED + INTERPOL RED &amp; YELLOW NOTICES · DEDUPED ON SOURCE ID — RE-IMPORTS NEVER DUPLICATE
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        <button data-testid="federal-import-fbi" onClick={importFbi} disabled={!!importing}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-head font-bold text-white rounded disabled:opacity-50" style={{ background: "#DC2626" }}>
          {importing === "FBI_DOJ" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} IMPORT FBI / DOJ WANTED
        </button>
        <button data-testid="federal-import-red" onClick={() => importInterpol("red")} disabled={!!importing}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-head font-bold text-white rounded disabled:opacity-50" style={{ background: "#B91C1C" }}>
          {importing === "INTERPOL_RED" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} IMPORT INTERPOL RED NOTICES
        </button>
        <button data-testid="federal-import-yellow" onClick={() => importInterpol("yellow")} disabled={!!importing}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-head font-bold text-black rounded disabled:opacity-50" style={{ background: "#F59E0B" }}>
          {importing === "INTERPOL_YELLOW" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} IMPORT INTERPOL YELLOW NOTICES
        </button>
      </div>
      {stats && (
        <div className="flex flex-wrap gap-2 mb-3 font-mono text-[10px]" data-testid="federal-stats">
          {Object.entries(SRC_META).map(([k, m]) => (
            <button key={k} data-testid={`federal-filter-${k}`} onClick={() => { const ns = src === k ? "" : k; setSrc(ns); search(q, ns); }}
              className="px-2 py-1 rounded border transition-colors"
              style={src === k ? { background: m.color, color: "#fff", borderColor: m.color } : { borderColor: "#2C2D35", color: m.color }}>
              {m.label}: {stats[k]?.count ?? 0}
            </button>
          ))}
          <span className="px-2 py-1 text-[#9CA3AF]">TOTAL ON FILE: {stats.total}</span>
        </div>
      )}
      <div className="flex gap-2 mb-3">
        <input data-testid="federal-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Search federal records by name, alias, nationality, DOB…"
          className="flex-1 bg-[#141518] border border-[#2C2D35] px-3 py-2.5 text-sm outline-none focus:border-[#7C3AED] font-mono" />
        <button data-testid="federal-search-btn" onClick={() => search()} disabled={busy}
          className="px-4 bg-[#7C3AED] text-white flex items-center gap-1.5 font-head text-sm">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
        </button>
      </div>
      {list.length === 0 && !busy && (
        <div className="text-sm font-mono text-[#9CA3AF] border border-[#2C2D35] p-3">
          No records on file{q ? " for that search" : ""}. Use the IMPORT buttons above to load the federal databases (safe to re-run — never duplicates).
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="federal-list">
        {list.map((r) => {
          const m = SRC_META[r.source] || { label: r.source, color: "#9CA3AF" };
          return (
            <div key={r.id} data-testid={`federal-card-${r.source_id}`} className="border border-[#2C2D35] bg-[#101114] flex gap-3 p-3">
              {r.image_url
                ? <img src={r.image_url} alt={r.name} loading="lazy" className="w-20 h-24 object-cover border border-[#2C2D35] shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                : <div className="w-20 h-24 border border-[#2C2D35] flex items-center justify-center shrink-0"><ShieldAlert size={22} className="text-[#4B5563]" /></div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="px-1.5 py-0.5 text-[9px] font-mono font-bold text-white rounded" style={{ background: m.color }}>{m.label}</span>
                  {r.kind && <span className="text-[9px] font-mono text-[#9CA3AF]">{r.kind}</span>}
                </div>
                <div className="font-head font-bold text-sm mt-1 text-white break-words">{r.name}</div>
                <div className="font-mono text-[10px] text-[#9CA3AF] mt-0.5">
                  {r.dob && <>DOB: {r.dob} · </>}{r.sex && <>{r.sex} · </>}{r.nationality && <>{r.nationality}</>}
                </div>
                {r.description && <div className="font-mono text-[10px] text-[#C9CDD4] mt-1 line-clamp-2">{r.description}</div>}
                <div className="flex gap-2 mt-1.5">
                  {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-[10px] font-mono flex items-center gap-1" style={{ color: m.color }}><ExternalLink size={10} /> SOURCE</a>}
                  {onAttach && r.source === "FBI_DOJ" && (
                    <button data-testid={`federal-attach-${r.source_id}`} onClick={() => onAttach({ uid: r.source_id, title: r.name, image_url: r.image_url, url: r.url })}
                      className="text-[10px] font-mono text-[#3B82F6]">ATTACH TO CALL</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="font-mono text-[10px] text-[#9CA3AF] mt-3 border border-[#2C2D35] p-2">
        Public data: FBI/DOJ Wanted Program (api.fbi.gov, includes DOJ fugitive posters) · INTERPOL Red Notices (wanted) &amp; Yellow Notices (missing). Re-imports UPSERT on the source record ID — duplicates are impossible. Verify with the originating agency before any enforcement action.
      </div>
    </div>
  );
}
